import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { LoginService } from '@shared-angular/services/login.service';
import { ReflactTrainAuthService } from '@shared-angular/services/reflact-train-auth.service';
import { UsertokenService } from '@shared-angular/services/usertoken.service';
import { MCPConfig } from '@shared/index';
import { firstValueFrom } from 'rxjs';
import { BotService } from './bot.service';
import { EventManagerService } from './event-manager/event-manager.service';
import { SocketService } from './socket.service';

/**
 *
 */
@Injectable({
  providedIn: 'root'
})
export class ReflactTrainService {

  private http = inject(HttpClient);
  private botService = inject(BotService);
  private socketService = inject(SocketService);
  private authService = inject(ReflactTrainAuthService);
  private usertokenService = inject(UsertokenService);
  private loginService = inject(LoginService);
  private eventManager = inject(EventManagerService);
  private loginPopup: Window | null = null;

  constructor() {
    void firstValueFrom(this.http.get<MCPConfig[]>("/api/user/mcp/getmanaged/" + this.botService.botConfig?._id)).then(async (v: MCPConfig[]) => {

      const reflactTrainMcp = v.find(mcp => mcp.type === "managed" && mcp.toolName === "reflactTrain");
      if (!reflactTrainMcp) return;

      // Manche reflact-Train-Installationen loggen den Nutzer serverseitig ein und haengen den Token direkt an die
      // Bot-URL, statt den Redirect-Flow zu durchlaufen - wird hier wie ein Redirect-Callback behandelt (unabhaengig von requiresAuth).
      const directToken = this.extractDirectAccessToken();
      if (directToken) {
        await this.consumeDirectAccessToken(directToken, reflactTrainMcp._id);
        return;
      }

      // requiresAuth erzwingt nur den Login SOFORT beim Boteinstieg - andernfalls wird erst bei einem Tool-Fehler mitten im Chat eingeloggt.
      if (reflactTrainMcp.requiresAuth) {
        void this.checkMcp(reflactTrainMcp._id);
      }
    });

    // Server hat einen bei einem Tool-Aufruf ungueltig gewordenen Token verworfen -> Nutzer erneut zum Login schicken.
    this.socketService.onReflactTrainAuthInvalid.subscribe(({ mcpId }) => void this.checkMcp(mcpId));

    // Die /auth/reflact-train-Route meldet sich hierueber, wenn sie im Login-Popup (statt top-level) landet.
    this.getAccessibleTopWindow()?.addEventListener("message", (event) => void this.handleAuthCallbackMessage(event));
  }

  // window.top existiert auch bei Cross-Origin-Einbettung (Bot-Iframe auf fremder Domain), der Zugriff auf seine
  // Properties wirft dann aber einen SecurityError - hier wird deshalb sicher geprueft, ob window.top nutzbar ist.
  private getAccessibleTopWindow(): Window | null {
    const topWindow = window.top;
    if (!topWindow) return null;
    try {
      void topWindow.location.href;
      return topWindow;
    } catch {
      return null;
    }
  }

  private extractDirectAccessToken(): string | null {
    const topWindow = this.getAccessibleTopWindow();
    if (!topWindow) return null;
    return new URLSearchParams(topWindow.location.search).get("train_mcp_access_token");
  }

  private async consumeDirectAccessToken(token: string, mcpId: string): Promise<void> {
    try {
      const result = await this.authService.authorizeReflactTrain({ token, mcpId });
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.REFLACTTRAIN.login", { mcpId });
      if (result.token) {
        this.usertokenService.setUserTokenAfterAuth(result.token);
        await this.loginService.loadMyUser();
      }
    } catch (error) {
      console.error("Error while consuming direct reflact Train access token:", error);
    } finally {
      this.stripAccessTokenFromUrl();
    }
  }

  private stripAccessTokenFromUrl(): void {
    const topWindow = this.getAccessibleTopWindow();
    if (!topWindow) return;
    const url = new URL(topWindow.location.href);
    url.searchParams.delete("train_mcp_access_token");
    topWindow.history.replaceState({}, "", url.toString());
  }

  private async checkMcp(mcpId: string): Promise<void> {
    const topWindow = this.getAccessibleTopWindow();
    if (!topWindow) return;

    // "state" traegt Ziel-MCP und Rueckweg zum Bot und wird direkt in die redirect_uri gehaengt, damit reflact Train
    // sie einfach unveraendert zurueckgeben kann, ohne einen eigenen "state"-Parameter unterstuetzen zu muessen.
    const path = topWindow.location.pathname.substring(3).split("/").filter(s => s != "").join("/");
    const state = btoa(JSON.stringify({ mcpId, path }));
    const redirect_uri = topWindow.location.origin + '/' + topWindow.location.pathname.split("/")[1] + "/auth/reflact-train?state=" + encodeURIComponent(state);

    const result = await firstValueFrom(this.http.post<{ valid: boolean, redirectUrl?: string }>(
      "/api/user/reflact-train/authcheck/" + mcpId,
      { redirect_uri }
    ));
    if (!result.valid && result.redirectUrl) {
      this.openLoginPopup(result.redirectUrl);
    }
  }

  // Login laeuft ab jetzt in einem Popup-Fenster statt per Vollseiten-Redirect, damit der Chat im Hintergrund erhalten bleibt.
  private openLoginPopup(loginUrl: string): void {
    const topWindow = this.getAccessibleTopWindow();
    if (!topWindow) return;
    this.loginPopup?.close();

    const url = new URL(loginUrl);
    url.searchParams.set("popup", "false");
    const features = this.buildPopupFeatures(topWindow);

    const popup = topWindow.open(url.toString(), "reflactTrainLogin", features);
    if (popup) {
      this.loginPopup = popup;
      return;
    }

    // Browser hat window.open() ohne direkte User-Geste blockiert -> Login erst nach Klick auf einen Hinweis oeffnen.
    this.showLoginPrompt(topWindow, () => {
      this.loginPopup = topWindow.open(url.toString(), "reflactTrainLogin", features);
    });
  }

  private buildPopupFeatures(topWindow: Window): string {
    const width = 600, height = 800;
    const left = topWindow.screenX + (topWindow.outerWidth - width) / 2;
    const top = topWindow.screenY + (topWindow.outerHeight - height) / 2;
    return `width=${String(width)},height=${String(height)},left=${String(left)},top=${String(top)}`;
  }

  private showLoginPrompt(topWindow: Window, onClick: () => void): void {
    const doc = topWindow.document;
    doc.getElementById('reflact-train-login-prompt')?.remove();

    const banner = doc.createElement('div');
    banner.id = 'reflact-train-login-prompt';
    banner.style.cssText = 'position:fixed;bottom:20px;right:20px;background:#1e1e1e;color:white;padding:14px 20px;border-radius:8px;cursor:pointer;z-index:2147483647;box-shadow:0 2px 10px rgba(0,0,0,0.3);font-family:sans-serif;font-size:14px;';
    banner.textContent = $localize`:@@reflact-train-login-prompt:Anmeldung bei reflact Train erforderlich – Hier klicken zum Einloggen`;
    banner.onclick = () => {
      banner.remove();
      onClick();
    };
    doc.body.appendChild(banner);
  }

  // Reagiert auf die postMessage der /auth/reflact-train-Route, wenn diese im Login-Popup landet.
  private async handleAuthCallbackMessage(event: MessageEvent): Promise<void> {
    const topWindow = this.getAccessibleTopWindow();
    if (!topWindow || event.origin !== topWindow.location.origin) return;
    const data = event.data as { type?: string, token?: string, mcpId?: string } | null;
    if (data?.type !== "reflactTrainAuthCallback" || !data.token || !data.mcpId) return;

    this.loginPopup?.close();
    this.loginPopup = null;

    try {
      const result = await this.authService.authorizeReflactTrain({ token: data.token, mcpId: data.mcpId });
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.REFLACTTRAIN.login", { mcpId: data.mcpId });
      if (result.token) {
        this.usertokenService.setUserTokenAfterAuth(result.token);
        await this.loginService.loadMyUser();
      }
    } catch (error) {
      console.error("Error while consuming reflact Train login from modal:", error);
    }
  }

}
